/**
 * CS Domain 花色图标 + 52 张卡的本地 SVG 图标
 */

import type { Suit } from '../engine/types'

// =============================================================================
// Domain 花色图标
// =============================================================================

export const DOMAIN_ICONS: Record<Exclude<Suit, 'wild'>, string> = {
  compute: 'lucide:cpu',
  data: 'lucide:database',
  network: 'lucide:network',
  infra: 'lucide:server',
}

export const DOMAIN_COLORS: Record<Suit, string> = {
  compute: '#3b82f6',
  data: '#22c55e',
  network: '#f59e0b',
  infra: '#8b5cf6',
  wild: '#ec4899',
}

// =============================================================================
// 52 张卡的本地 SVG 图标 (4 domain × 13 rank)
// =============================================================================

/** Vite eager glob import — 所有 SVG 打包为 asset URL */
const iconModules = import.meta.glob('../assets/icons/*.svg', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

/** 从 glob 结果构建 key → URL 映射 (e.g. "compute_2" → "/assets/compute_2-xxxx.svg") */
const LOCAL_ICON_URLS: Record<string, string> = {}
for (const [path, url] of Object.entries(iconModules)) {
  // path looks like "../assets/icons/compute_2.svg"
  const match = path.match(/\/([^/]+)\.svg$/)
  if (match) LOCAL_ICON_URLS[match[1]!] = url
}

// =============================================================================
// 52 张卡的 Iconify 图标 ID (仅保留作为元数据参考)
// =============================================================================

export const CARD_FACE_ICONS: Record<string, string> = {
  // Compute (计算) — 从代码到部署
  compute_2: 'devicon:python',
  compute_3: 'devicon:nodejs',
  compute_4: 'devicon:go',
  compute_5: 'devicon:java',
  compute_6: 'devicon:rust',
  compute_7: 'devicon:docker',
  compute_8: 'devicon:nomad',
  compute_9: 'logos:vmware',
  compute_10: 'devicon:openstack',
  compute_11: 'devicon:apachespark',
  compute_12: 'logos:openshift',
  compute_13: 'devicon:kubernetes',
  compute_14: 'devicon:wasm',

  // Data (数据) — 数据库、缓存、消息、搜索
  data_2: 'devicon:sqlite',
  data_3: 'devicon:memcached',
  data_4: 'devicon:redis',
  data_5: 'devicon:mysql',
  data_6: 'devicon:mariadb',
  data_7: 'devicon:postgresql',
  data_8: 'devicon:mongodb',
  data_9: 'devicon:rabbitmq',
  data_10: 'devicon:elasticsearch',
  data_11: 'logos:kafka-icon',
  data_12: 'devicon:cassandra',
  data_13: 'devicon:neo4j',
  data_14: 'simple-icons:snowflake',

  // Network (网络) — 工具 → 服务器 → 代理 → 平台 → 网格
  network_2: 'simple-icons:wireshark',
  network_3: 'devicon:apache',
  network_4: 'simple-icons:caddy',
  network_5: 'simple-icons:wireguard',
  network_6: 'devicon:nginx',
  network_7: 'devicon:haproxy',
  network_8: 'devicon:postman',
  network_9: 'devicon:traefikproxy',
  network_10: 'devicon:envoy',
  network_11: 'logos:grpc',
  network_12: 'logos:kong-icon',
  network_13: 'devicon:cloudflare',
  network_14: 'devicon:istio',

  // Infra (基础设施) — CI/CD、监控、IaC、运维
  infra_2: 'devicon:git',
  infra_3: 'devicon:jenkins',
  infra_4: 'devicon:ansible',
  infra_5: 'devicon:prometheus',
  infra_6: 'devicon:grafana',
  infra_7: 'devicon:vault',
  infra_8: 'devicon:terraform',
  infra_9: 'devicon:argocd',
  infra_10: 'devicon:githubactions',
  infra_11: 'devicon:datadog',
  infra_12: 'simple-icons:splunk',
  infra_13: 'simple-icons:pagerduty',
  infra_14: 'devicon:gitlab',
}

/** 判断图标是否需要手动上色 (lucide/simple-icons 是单色，需要上色) */
export function iconNeedsColor(iconId: string): boolean {
  return iconId.startsWith('lucide:') || iconId.startsWith('simple-icons:') || iconId.startsWith('mdi:')
}

// =============================================================================
// 52 张卡的名称标签
// =============================================================================

export const CARD_MASCOT_LABELS: Record<string, string> = {
  // Compute — 从代码到部署
  compute_2: 'Python',
  compute_3: 'Node.js',
  compute_4: 'Go',
  compute_5: 'Java',
  compute_6: 'Rust',
  compute_7: 'Docker',
  compute_8: 'Nomad',
  compute_9: 'VMware',
  compute_10: 'OpenStack',
  compute_11: 'Spark',
  compute_12: 'OpenShift',
  compute_13: 'Kubernetes',
  compute_14: 'Wasm',

  // Data — 数据库、缓存、消息、搜索
  data_2: 'SQLite',
  data_3: 'Memcached',
  data_4: 'Redis',
  data_5: 'MySQL',
  data_6: 'MariaDB',
  data_7: 'PostgreSQL',
  data_8: 'MongoDB',
  data_9: 'RabbitMQ',
  data_10: 'Elastic',
  data_11: 'Kafka',
  data_12: 'Cassandra',
  data_13: 'Neo4j',
  data_14: 'Snowflake',

  // Network — 工具 → 服务器 → 代理 → 平台 → 网格
  network_2: 'Wireshark',
  network_3: 'Apache',
  network_4: 'Caddy',
  network_5: 'WireGuard',
  network_6: 'Nginx',
  network_7: 'HAProxy',
  network_8: 'Postman',
  network_9: 'Traefik',
  network_10: 'Envoy',
  network_11: 'gRPC',
  network_12: 'Kong',
  network_13: 'Cloudflare',
  network_14: 'Istio',

  // Infra — CI/CD、监控、IaC、运维
  infra_2: 'Git',
  infra_3: 'Jenkins',
  infra_4: 'Ansible',
  infra_5: 'Prometheus',
  infra_6: 'Grafana',
  infra_7: 'Vault',
  infra_8: 'Terraform',
  infra_9: 'ArgoCD',
  infra_10: 'GH Actions',
  infra_11: 'Datadog',
  infra_12: 'Splunk',
  infra_13: 'PagerDuty',
  infra_14: 'GitLab',
}

// =============================================================================
// Iconify REST API URL 构建 (仅用于花色小图标)
// =============================================================================

/**
 * 构建 Iconify REST API URL
 * Format: https://api.iconify.design/{prefix}/{name}.svg?height={size}
 */
export function getIconifyUrl(iconId: string, size: number = 24, color?: string): string {
  const lastColon = iconId.lastIndexOf(':')
  if (lastColon === -1) return ''
  const prefix = iconId.slice(0, lastColon)
  const name = iconId.slice(lastColon + 1)
  let url = `https://api.iconify.design/${prefix}/${name}.svg?height=${size}`
  if (color) url += `&color=${encodeURIComponent(color)}`
  return url
}

/**
 * 获取卡牌正面图标 URL — 优先使用本地 SVG，回退到 Iconify API
 */
export function getCardFaceIconUrl(suit: string, rank: number, size: number = 40): string | undefined {
  const key = `${suit}_${rank}`
  // 优先本地
  if (LOCAL_ICON_URLS[key]) return LOCAL_ICON_URLS[key]
  // 回退远程
  const iconId = CARD_FACE_ICONS[key]
  if (!iconId) return undefined
  const color = iconNeedsColor(iconId) ? (DOMAIN_COLORS[suit as Suit] ?? '#ffffff') : undefined
  return getIconifyUrl(iconId, size, color)
}

/**
 * 获取花色图标 URL
 */
export function getSuitIconUrl(suit: Suit, size: number = 16): string {
  if (suit === 'wild') {
    return getIconifyUrl('lucide:sparkles', size, DOMAIN_COLORS.wild)
  }
  return getIconifyUrl(DOMAIN_ICONS[suit], size, DOMAIN_COLORS[suit])
}
